import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DollarSign, Save, Plus, Trash2, Link as LinkIcon, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const TEAL = "#0D9488";

/** Coach-facing card on MyClients — configure external payment links + pricing plans.
 *  All payments are handled OFF-PLATFORM. This card only advertises how a client
 *  can pay the coach directly (Venmo, PayPal, custom link).
 */
export default function CoachPaymentSetupCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    payment_url: "",
    payment_venmo: "",
    payment_paypal: "",
    payment_note: "",
    payment_plans: [],
  });

  useEffect(() => {
    api.get("/coach/profile")
      .then(({ data }) => setForm({
        payment_url: data.payment_url || "",
        payment_venmo: data.payment_venmo || "",
        payment_paypal: data.payment_paypal || "",
        payment_note: data.payment_note || "",
        payment_plans: data.payment_plans || [],
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const patchPlans = (nextPlans) => setForm((f) => ({ ...f, payment_plans: nextPlans }));

  const addPlan = () => {
    if (form.payment_plans.length >= 6) {
      toast.error("You can list up to 6 plans.");
      return;
    }
    patchPlans([...form.payment_plans, { label: "", price: "", cadence: "", description: "" }]);
  };

  const updatePlan = (i, field, value) => {
    const next = form.payment_plans.map((p, idx) => (idx === i ? { ...p, [field]: value } : p));
    patchPlans(next);
  };

  const removePlan = (i) => patchPlans(form.payment_plans.filter((_, idx) => idx !== i));

  const validPlans = () => form.payment_plans.every((p) => p.label.trim() && p.price.trim());

  const save = async () => {
    if (!validPlans()) {
      toast.error("Every plan needs a label and price.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/coach/payment", {
        payment_url: form.payment_url.trim(),
        payment_venmo: form.payment_venmo.trim(),
        payment_paypal: form.payment_paypal.trim(),
        payment_note: form.payment_note.trim(),
        payment_plans: form.payment_plans.map((p) => ({
          label: p.label.trim(),
          price: p.price.trim(),
          cadence: (p.cadence || "").trim() || null,
          description: (p.description || "").trim() || null,
        })),
      });
      toast.success("Payment settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-xl p-6 h-52 animate-pulse" data-testid="coach-payment-loading" />;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coach-payment-card">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Payment settings</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Your pricing &amp; payment links</h3>
      <p className="text-sm text-slate-500 mb-5 leading-relaxed">
        You handle payments <b>outside</b> the platform. List whatever plans you offer and how clients can pay you. Anything you fill in here is shown to interested clients on your directory profile — leave blank to hide.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <Field
          label="Custom link (Stripe, Buy-me-a-coffee, calendar, etc.)"
          testid="payment-url"
          icon={LinkIcon}
          value={form.payment_url}
          onChange={(v) => setForm((f) => ({ ...f, payment_url: v }))}
          placeholder="https://…"
        />
        <Field
          label="Venmo handle"
          testid="payment-venmo"
          value={form.payment_venmo}
          onChange={(v) => setForm((f) => ({ ...f, payment_venmo: v }))}
          placeholder="@yourhandle"
        />
        <Field
          label="PayPal link or email"
          testid="payment-paypal"
          value={form.payment_paypal}
          onChange={(v) => setForm((f) => ({ ...f, payment_paypal: v }))}
          placeholder="paypal.me/you or you@example.com"
        />
        <div>
          <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
            Client-facing note (optional)
          </label>
          <Textarea
            value={form.payment_note}
            onChange={(e) => setForm((f) => ({ ...f, payment_note: e.target.value.slice(0, 400) }))}
            rows={2}
            placeholder="e.g. Payments due weekly on Sunday. DM before your first session."
            className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
            data-testid="payment-note-input"
          />
          <div className="text-[10px] text-slate-400 font-mono-num mt-1">{form.payment_note.length}/400</div>
        </div>
      </div>

      {/* Plans list */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Plans</span>
          <span className="text-[10px] text-slate-400 tabular-nums">{form.payment_plans.length}/6</span>
          <Button
            type="button"
            onClick={addPlan}
            size="sm"
            variant="ghost"
            disabled={form.payment_plans.length >= 6}
            className="ml-auto text-teal-700 hover:bg-teal-50 h-7 px-2"
            data-testid="payment-plan-add"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add plan
          </Button>
        </div>
        {form.payment_plans.length === 0 ? (
          <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4 text-center">
            No plans yet — add one to publish pricing to clients.
          </div>
        ) : (
          <div className="space-y-2" data-testid="payment-plans-list">
            {form.payment_plans.map((p, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3" data-testid={`payment-plan-row-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_auto] gap-2 items-start">
                  <Input value={p.label} onChange={(e) => updatePlan(i, "label", e.target.value)}
                         placeholder="Label · e.g. Weekly check-in"
                         className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                         data-testid={`payment-plan-label-${i}`} />
                  <Input value={p.price} onChange={(e) => updatePlan(i, "price", e.target.value)}
                         placeholder="$60"
                         className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                         data-testid={`payment-plan-price-${i}`} />
                  <Input value={p.cadence || ""} onChange={(e) => updatePlan(i, "cadence", e.target.value)}
                         placeholder="per week"
                         className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
                         data-testid={`payment-plan-cadence-${i}`} />
                  <Button type="button" size="sm" variant="ghost" onClick={() => removePlan(i)}
                          className="text-rose-500 hover:bg-rose-50"
                          data-testid={`payment-plan-remove-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Input
                  value={p.description || ""}
                  onChange={(e) => updatePlan(i, "description", e.target.value)}
                  placeholder="Optional description of what's included"
                  className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] mt-2 text-sm"
                  data-testid={`payment-plan-desc-${i}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 mb-4 text-[12px] text-amber-900 leading-relaxed">
        <b>Reminder:</b> The Amino Exchange does <b>not</b> process any of these payments. You handle billing directly with your clients through your own payment link, Venmo, or PayPal.
      </div>

      <div className="flex items-center justify-end">
        <Button type="button" onClick={save} disabled={saving}
                className="text-white" style={{ background: TEAL }}
                data-testid="payment-settings-save">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save payment settings"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, icon: Icon }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
